import type { PublicCommentNode } from '@govinity/validation/public';

export const PUBLIC_COMMENT_MAX_ATTACHMENT_SIZE = 5 * 1024 * 1024;

export const publicCommentOptionsQueryKey = (governmentCode: string, meetingId: number) => [
  'portal-public-comment-options',
  governmentCode,
  meetingId,
];

export const publicCommentUserCommentsQueryKey = (governmentCode: string, meetingId: number) => [
  'portal-public-comment-user-comments',
  governmentCode,
  meetingId,
];

/**
 * A legislative item only identifies a comment target together with the section it is placed on, so
 * the option tree is flattened into entries carrying both ids next to the label they render as.
 */
export type PublicCommentEntry = {
  key: string;
  name: string;
  depth: number;
  disabled: boolean;
  agendaSectionId: number;
  legislativeItemId?: number;
};

export const flattenCommentEntries = (
  nodes: PublicCommentNode[],
  depth = 0,
  parentSectionId?: number
): PublicCommentEntry[] =>
  nodes.flatMap((node) => {
    if (node.type === 'item') {
      if (parentSectionId === undefined) {
        return [];
      }

      return [
        {
          key: `item:${parentSectionId}:${node.id}`,
          name: node.name,
          depth,
          disabled: node.disabled,
          agendaSectionId: parentSectionId,
          legislativeItemId: node.id,
        },
      ];
    }

    return [
      {
        key: `section:${node.id}`,
        name: node.name,
        depth,
        disabled: node.disabled,
        agendaSectionId: node.id,
      },
      ...flattenCommentEntries(node.children, depth + 1, node.id),
    ];
  });

/** Whether the tree offers anything a citizen can actually comment on. */
export const hasSelectableEntry = (entries: PublicCommentEntry[]): boolean =>
  entries.some(({ disabled }) => !disabled);
